import React, { useState, useRef } from 'react';
import { contactInfo, serviceOptions } from '../data/content';
import useReveal from '../hooks/useReveal';
import './Contact.css';

/* ─────────────────────────────────────────────────────────────
   EMAILJS CONFIG
   1. Sign up free at https://www.emailjs.com
   2. Create a Service (Gmail, Outlook, etc.) → copy the Service ID
   3. Create an Email Template            → copy the Template ID
   4. Your Public Key is initialised in   public/index.html
   ───────────────────────────────────────────────────────────── */
const EMAILJS_SERVICE_ID  = 'service_cqdp9ev';   // e.g. 'service_abc123'
const EMAILJS_TEMPLATE_ID = 'template_qyiv5hd';  // e.g. 'template_xyz789'

function ContactCard({ href, icon, label, val }) {
  const Tag = href ? 'a' : 'div';
  return (
    <Tag href={href} className="contact-card">
      <div className="contact-card__icon">{icon}</div>
      <div>
        <div className="contact-card__label">{label}</div>
        <div className="contact-card__val">{val}</div>
      </div>
    </Tag>
  );
}

function FormField({ label, id, children }) {
  return (
    <div className="form-group">
      <label className="form-label" htmlFor={id}>{label}</label>
      {children}
    </div>
  );
}

export default function Contact() {
  const leftRef  = useReveal();
  const rightRef = useReveal();
  const formRef  = useRef(null);

  const [form, setForm] = useState({
    fname: '', lname: '', email: '', phone: '', service: '', message: '',
  });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [error, setError] = useState('');

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fname.trim() || !form.email.trim()) {
      setError('Please fill in at least your first name and email address.');
      return;
    }
    setError('');
    setStatus('sending');

    /*
      EmailJS template variables — use these names in your template:
        {{from_name}}         Full name
        {{from_email}}        Sender email
        {{reply_to}}          Same as email (so you can hit Reply)
        {{phone}}             Phone number
        {{service_requested}} Chosen service
        {{message}}           Message body
    */
    const templateParams = {
      from_name:         `${form.fname} ${form.lname}`.trim(),
      from_email:        form.email,
      reply_to:          form.email,
      phone:             form.phone || 'Not provided',
      service_requested: form.service || 'Not specified',
      message:           form.message || 'No message provided',
    };

    try {
      await window.emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams);
      setStatus('success');
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('error');
    }
  };

  return (
    <section className="contact" id="contact">
      <div className="contact__layout">

        {/* Left */}
        <div className="contact__info" ref={leftRef}>
          <span className="section-label">Get In Touch</span>
          <h2 className="section-title">
            We'd Love to<br />
            <em>Hear From You</em>
          </h2>
          <p className="section-body">
            Reach out today for reliable, professional cleaning services. We're
            happy to discuss your needs and offer competitive, negotiable rates.
          </p>
          <div className="contact__cards">
            {contactInfo.map((c) => <ContactCard key={c.label} {...c} />)}
          </div>
        </div>

        {/* Right */}
        <div className="contact__form-wrap" ref={rightRef}>
          {status === 'success' ? (
            <div className="form-success">
              <div className="form-success__icon">✅</div>
              <h4>Message Sent!</h4>
              <p>
                Thank you, <strong>{form.fname}</strong>. A member of the Fresh Wave
                team will be in touch within 24 hours.
              </p>
              <button
                className="form-reset"
                onClick={() => {
                  setForm({ fname: '', lname: '', email: '', phone: '', service: '', message: '' });
                  setStatus('idle');
                }}
              >
                Send another message
              </button>
            </div>
          ) : (
            <form className="contact-form" ref={formRef} onSubmit={handleSubmit} noValidate>
              <p className="form-title">Send Us a Message</p>
              <p className="form-subtitle">We'll get back to you within 24 hours.</p>

              <div className="form-row">
                <FormField label="First Name" id="fname">
                  <input className="form-input" type="text" id="fname" name="fname"
                    placeholder="Jane" value={form.fname} onChange={handleChange} />
                </FormField>
                <FormField label="Last Name" id="lname">
                  <input className="form-input" type="text" id="lname" name="lname"
                    placeholder="Smith" value={form.lname} onChange={handleChange} />
                </FormField>
              </div>

              <FormField label="Email Address" id="email">
                <input className="form-input" type="email" id="email" name="email"
                  placeholder="jane@example.com" value={form.email} onChange={handleChange} />
              </FormField>

              <FormField label="Phone Number" id="phone">
                <input className="form-input" type="tel" id="phone" name="phone"
                  placeholder="07700 000000" value={form.phone} onChange={handleChange} />
              </FormField>

              <FormField label="Service Required" id="service">
                <select className="form-input form-select" id="service" name="service"
                  value={form.service} onChange={handleChange}>
                  <option value="" disabled>Select a service…</option>
                  {serviceOptions.map((opt) => <option key={opt}>{opt}</option>)}
                </select>
              </FormField>

              <FormField label="Your Message" id="message">
                <textarea className="form-input form-textarea" id="message" name="message"
                  placeholder="Tell us about your property, preferred dates, or any specific requirements…"
                  value={form.message} onChange={handleChange} rows={4} />
              </FormField>

              {error && <p className="form-error">{error}</p>}
              {status === 'error' && (
                <p className="form-error">
                  Something went wrong. Please try again or call us on 07926 395614.
                </p>
              )}

              <button type="submit" className="form-submit" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : 'Send Message →'}
              </button>
            </form>
          )}
        </div>

      </div>
    </section>
  );
}
